import { createReducer, on } from '@ngrx/store';
import {
    DrawingToolConfig,
    DrawingToolName,
} from "shared/classroom/toolbar/core";
import { getStoredColor, getStoredStrokeWidth} from './toolbar-config-storage';
import { getStoredToolbarPosition } from "./toolbar-config-session-storage";
import * as ToolbarActions from './toolbar.actions';

export const toolbarFeatureKey = 'slideSyncToolbar';

export interface State {
    config: DrawingToolConfig;

    /**
     * Currently activated drawing tool (pencil, ellipse, selection, arrow).
     * Only one drawing tool can be activated at a time.
     */
    activatedDrawingTool: null | DrawingToolName;

    /**
     * Drawing tools can be disabled e.g. if a slide type does not support drawing.
     * If `false`, no drawing tool can be selected from the toolbar.
     */
    drawingToolsEnabled: boolean;

    /**
     * Whether to bookpage tool can be activated or not (`false` = disabled).
     * The tool should be disabled if there are no bookpages available.
     */
    bookpageToolEnabled: boolean;

    /**
     * Whether the bookpage tool is current activated.
     * Can only be true if it's enabled (= activateable).
     */
    bookpageToolActivated: boolean;

    /**
     * If `true`, the user has opened the color palette tool in order to choose a different color.
     * If `false` the color palette tool should be collapsed and not be shown.
     * This setting is independent from other activateable tools,
     * e.g. the pencil tool and the color palette tool can be activated simultaneously.
     */
    colorPaleteActivated: boolean;

    drawingMenuOpened: boolean;

    drawingMenuPosition: 'left' | 'right';

    /**
     * If `true`, the user has opened the slide-sync toolbar.
     * If `false` the slide-sync toolbar should be collapsed and not be shown.
     * Used for slide-sync toolbar when it can be opened via 'next-slides-preview.component.html'
     * (SLIDE_SYNC_NAVIGATION_PREVIEW_ENABLED injection token is set to true in this case)
     */
    floatingToolbarOpened: boolean;

    // `goToParentInProcess` moved to `ToolbarUiStateService` in Stage D.6
    // (write-path refactor). The reducer used to listen to four actions
    // (toolbarNavigateToParent → true; show*SlideInPreviewScreen ×3 +
    // setNavigationPreviewBasicSlides → false). The reset triggers were
    // state-mirrors of Y.Doc layer-slide Signals + the
    // navigationPreviewBasicSlides Y.Doc field, which the new Signal-based
    // service watches directly via `ClassroomFacade`.

}

export const initialState: State = {
    activatedDrawingTool: null,
    bookpageToolEnabled: false,
    bookpageToolActivated: false,
    drawingToolsEnabled: true,
    colorPaleteActivated: false,
    drawingMenuOpened: false,
    floatingToolbarOpened: false,
    drawingMenuPosition: 'left',
    config: {
        // null on purpose: `ToolbarEffects.initStrokeColor$` seeds the default from
        // SLIDE_SYNC_TOOLBAR_CONFIG.colorDefault, because a reducer cannot inject a token.
        strokeColor: getStoredColor() || null,
        strokeWidth: getStoredStrokeWidth() || 10,
        toolbarPosition: getStoredToolbarPosition() || null,
    },
};

export const reducer = createReducer(
    initialState,

    on(
        ToolbarActions.enableDrawingTools,
        (state): State => ({
            ...state,
            drawingToolsEnabled: true,
        })
    ),
    on(
        ToolbarActions.disableDrawingTools,
        (state, action): State => ({
            ...state,
            drawingToolsEnabled: false,
        })
    ),

    /**
     * Deactivate all curently activated drawing(!) tools.
     * NOTE: deactive !== disable
     */
    on(
        ToolbarActions.deactivateAllTools,
        (state, action): State => ({
            ...state,
            activatedDrawingTool: null,
            colorPaleteActivated: false,
            drawingMenuOpened: false,
            bookpageToolActivated: false,
        })
    ),

    /**
     * Replaces the whole stored config object at once.
     */
    on(
        ToolbarActions.updateConfig,
        (state, { config }): State => ({
            ...state,
            config,
        })
    ),

    /**
     * User has selected a stroke color from the color palette tool.
     * Updates the config to use the selected stroke color from now on.
     */
    on(
        ToolbarActions.strokeColorSelected,
        (state, { strokeColor }): State => ({
            ...state,
            colorPaleteActivated: false,
            config: {
                ...state.config,
                strokeColor,
            },
        })
    ),

    /**
     * Startup seed only. Unlike `strokeColorSelected` it must not close the color palette,
     * because no user interaction happened.
     */
    on(
        ToolbarActions.strokeColorInitialized,
        (state, { strokeColor }): State => ({
            ...state,
            config: {
                ...state.config,
                strokeColor,
            },
        })
    ),

    on(
        ToolbarActions.strokeWidthSelected,
        (state, { strokeWidth }): State => ({
            ...state,
            config: {
                ...state.config,
                strokeWidth,
            },
        })
    ),

    /**
     * (De-)activate the color palette tool without any impact on other tools.
     */
    on(
        ToolbarActions.toggleColorPalette,
        (state): State => ({
            ...state,
            colorPaleteActivated: !state.colorPaleteActivated,
        })
    ),

    /**
     * (De-)activate the drawing menu tool without any impact on other tools.
     */
    on(
        ToolbarActions.openDrawingMenu,
        (state): State => ({
            ...state,
            drawingMenuOpened: true,
        })
    ),

    /**
     * (De-)activate the drawing menu tool without any impact on other tools.
     */
    on(
        ToolbarActions.closeDrawingMenu,
        (state): State => ({
            ...state,
            drawingMenuOpened: false,
        })
    ),

     /**
     * Shows the slide-sync toolbar
     */
     on(
        ToolbarActions.openFloatingToolbar,
        (state): State => ({
            ...state,
            floatingToolbarOpened: true,
        })
    ),

    /**
     * Hides the slide-sync toolbar
     */
    on(
        ToolbarActions.closeFloatingToolbar,
        (state): State => ({
            ...state,
            floatingToolbarOpened: false,
        })
    ),

    /**
     * Enable or disable a specific drawing tool.
     * Replace any other drawing tool which was activated before.
     */
    on(
        ToolbarActions.toggleDrawingTool,
        (state, { toolName }): State => ({
            ...state,
            activatedDrawingTool:
                state.activatedDrawingTool === toolName ? null : toolName,
        })
    ),

    /**
     * Enable a specific drawing tool.
     * Replace any other drawing tool which was activated before.
     */
    on(
        ToolbarActions.activateDrawingTool,
        (state, { toolName }): State => ({
            ...state,
            activatedDrawingTool: toolName,
        })
    ),

    /**
     * Enabling the bookpage tool makes it activateable (can be clicked).
     */
    on(
        ToolbarActions.enableBookpageTool,
        (state): State => ({
            ...state,
            bookpageToolEnabled: true,
        })
    ),

    /**
     * Disabling the bookpage tool makes it non-clickable.
     */
    on(
        ToolbarActions.disableBookpageTool,
        (state): State => ({
            ...state,
            bookpageToolEnabled: false,
        })
    ),

    on(
        ToolbarActions.toggleBookpageTool,
        (state): State => ({
            ...state,
            bookpageToolActivated: !state.bookpageToolActivated,
        })
    ),
    // The two reducer handlers that tracked `goToParentInProcess` (set true
    // on toolbarNavigateToParent; set false on show*SlideInPreviewScreen /
    // setNavigationPreviewBasicSlides) were deleted in Stage D.6 — the flag
    // lives on `ToolbarUiStateService` now. See its docstring.
    on(
        ToolbarActions.setToolbarPosition,
        (state, { position }): State => ({
            ...state,
            colorPaleteActivated: false,
            config: {
                ...state.config,
                toolbarPosition: position,
            },
        })
    ),
    on(
        ToolbarActions.setSubmenuToolbarPosition,
        (state, { position }): State => ({
            ...state,
            drawingMenuPosition: position,
        })
    ),
);
